import { useTranslation } from "react-i18next";
import { Play, Square, RotateCw, RefreshCw, Server, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useServices, useServiceAction } from "@/api/queries";
import { cn } from "@/lib/utils";

export function Services() {
  const { t } = useTranslation();
  const { data, isLoading, refetch } = useServices();
  const action = useServiceAction();

  const onAction = async (name: string, act: "start" | "stop" | "restart") => {
    try {
      const result = await action.mutateAsync({ name, action: act });
      if (result.ok) {
        // toast handled
      }
    } catch {
      // toast
    }
  };

  // Показываем только реально развёрнутые сервисы (исключаем not_found)
  const items = ((data?.items ?? []) as any[]).filter((s) => s.status !== "not_found");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Сервисы
          </h1>
          <p className="text-muted-foreground mt-1">
            Управление развёрнутыми OSINT-сервисами
          </p>
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4" />
          Обновить
        </Button>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center p-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card className="border-dashed">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="h-4 w-4" /> Нет активных сервисов
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            На этом тенанте не развёрнуты OSINT-сервисы. Для запуска воспользуйтесь скриптом
            <code className="mx-1 px-1 rounded bg-muted">./deploy/start-osint-stack.sh</code>.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((svc: any) => (
            <Card key={svc.name} className="overflow-hidden">
              <div
                className={cn(
                  "h-1",
                  svc.status === "running"
                    ? svc.health === "healthy"
                      ? "bg-emerald-500"
                      : "bg-amber-500"
                    : svc.status === "not_found"
                      ? "bg-zinc-500"
                      : "bg-red-500",
                )}
              />
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 font-mono text-sm">
                  <Server className="h-4 w-4 text-muted-foreground" />
                  {svc.name}
                </CardTitle>
                <div className="flex items-center gap-2 text-xs">
                  {svc.status === "running" ? (
                    svc.health === "healthy" ? (
                      <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-500">
                        <CheckCircle2 className="mr-1 h-3 w-3" />
                        Работает
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-500">
                        <AlertCircle className="mr-1 h-3 w-3" />
                        Нестабильно
                      </Badge>
                    )
                  ) : (
                    <Badge variant="outline" className="text-[10px]">
                      {svc.status}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {svc.image && (
                  <div className="text-[10px] text-muted-foreground">
                    <span className="font-semibold">образ:</span> {svc.image}
                  </div>
                )}
                {svc.ports && (
                  <div className="text-[10px] text-muted-foreground">
                    <span className="font-semibold">порты:</span> {svc.ports}
                  </div>
                )}
                <div className="flex gap-1 pt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 flex-1 text-xs"
                    disabled={svc.status === "running" || action.isPending}
                    onClick={() => onAction(svc.name, "start")}
                  >
                    <Play className="h-3 w-3" />
                    Запуск
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 flex-1 text-xs"
                    disabled={svc.status !== "running" || action.isPending}
                    onClick={() => onAction(svc.name, "stop")}
                  >
                    <Square className="h-3 w-3" />
                    Стоп
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 flex-1 text-xs"
                    disabled={action.isPending}
                    onClick={() => onAction(svc.name, "restart")}
                  >
                    <RotateCw className="h-3 w-3" />
                    Рестарт
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
